import { Button, Dialog, Icon, useNavigate } from '@startlibs/components'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash/fp'
import { ADMIN } from '../../enums/UserRoles'
import { DISPLAY } from '../../enums/UploaderManagement'
import { RecordsManagerDirectMessage } from '../../request/RecordsManagerDirectMessage'
import { jwtGetFetcher } from '../../utils/authFetch'
import { getJwt } from '../../hooks/useJwt'
import { getStorageHost, setStorageHost } from '../../hooks/useStorageHost'
import { getUploaderJwt, setUploaderJwt } from '../../hooks/useUploaderJwt'
import { LoadingPage } from '../../components/PageLoading'
import styled, { css } from 'styled-components'
import { convertBytesAutoUnit, isUnderMBLimit } from '../../utils/utils'
import { getColor, callIfFunction } from '@startlibs/utils'
import { useIntl } from 'react-intl'
import { setNotification } from '../../components/Notifications'

const WaitingBox = styled.div`
  background: ${getColor('lightYellow')};
  color: rgba(0,0,0,0.65);
  display: flex;
  align-items: center;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid ${getColor('gray210')};
  border-radius: 6px;
  ${props => props.small == true && css`
    padding: 12px;
    margin-bottom: 1rem;
    border: 0px;
  `}
  .waitingBoxContent {
    position: relative;
    padding-left: 5.5rem;
    p {
      margin-bottom: 0;
    }
    ${Icon} {
      font-size: 62px;
      position: absolute;
      left: 0;
      top: 0;
    }
    ${props => props.small == true && css`
    padding-left: 3.8rem;
    ${Icon} {
      font-size: 24px;
      width: 3rem;
      height: 3rem;
      line-height: 3rem;
      text-align: center;
      margin-right: 1.5rem;
      color: black;
      background: rgba(0,0,0,0.075);
      border-radius: 50%;
      position: absolute;
      left: 0;
      top: 0;
    }
  `}
  } 
`

const FooterDiv = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 2rem;
`

const SizeError = styled.div`
  padding: 1rem 3.5rem 1rem 1rem;
  border-radius: 8px;
  position: relative;
  text-align: left;
  font-size: 13px;
  line-height: 130%;
  background: rgb(195, 40, 45);
  color: white;
  border: 0px;
`

const SizeLine = styled.div`
  text-align: right;
  margin: 1rem 0rem;
  font-size: 13px;
`

export const DirectMessageRecordsSelectorDialog = ({
  fileDialog,
  caseRequest,
  setCaseRequest,
  selectedDirectMessageRecords,
  setSelectedDirectMessageRecords,
  extraAttachmentBytes = 0,
  sizeLimitMB = 3.5,
}) => {
  const uploaderRef = useRef()
  const caseId = caseRequest.requestCode;
  const navigate = useNavigate()
  const intl = useIntl()
  const { medicalRecordsInfo } = caseRequest

  const [isEffectProcessing, setIsEffectProcessing] = useState(false);
  const [caseLoaded, setCaseLoaded] = useState(false);

  var caseUID = ''
  var listModeRes = ''
  var patientUID = ''
  var userRole = 0

  useEffect(() => {
    setIsEffectProcessing(true);
    jwtGetFetcher(getJwt())(`/api/storageToken`, { requestId: caseRequest.requestId })
      .then((response) => {
        let responseJson = JSON.parse(response.jwt);
        setStorageHost(response.storageHost)
        setUploaderJwt(responseJson.jwt)
        caseUID = responseJson.caseUID
        listModeRes = responseJson.listMode
        patientUID = responseJson.patientUID
        userRole = responseJson.userRole
        return caseId
          ? jwtGetFetcher(getUploaderJwt())(getStorageHost() + "/case/" + caseId + (patientUID ? "?patientUID=" + patientUID : "")) //+(patientUID ? "&collapse=true" : "?collapse=true"))
          : jwtGetFetcher(getUploaderJwt())(getStorageHost() + "/record/patient/" + patientUID).then(medicalRecords => ({ medicalRecords }))
      })
      .then((response) => {
        setCaseRequest(caseRequest => ({
          ...caseRequest,
          medicalRecords: response.medicalRecords,
          group: response.group
        }));
        setCaseLoaded(true)
        setIsEffectProcessing(false);
      })
      .catch(([, { status }]) => {
        if (status === 569) {
          navigate("/access")
          setNotification({
            type: "alert", msg: intl.formatMessage({
              defaultMessage: "This case is locked",
              description: "Medical records, case locked notification"
            })
          })
        }
      })

    return () => { setCaseRequest(_.unset("medicalRecords")); setCaseLoaded(false) }

  }, [])

  const attachmentsBytes = useMemo(() => {
    const medicalRecords = caseRequest.medicalRecords || [];
    return selectedDirectMessageRecords.reduce((acc, record) => {
      const numericId = Number(record);
      const matched = medicalRecords.find(r => Number(r.id) === numericId);
      return acc + (matched?.fileSize || 0);
    }, 0);
  }, [caseRequest.medicalRecords, selectedDirectMessageRecords]);

  const totalBytes = attachmentsBytes + extraAttachmentBytes;
  const totalSize = convertBytesAutoUnit(totalBytes);
  const totalSizeUnderLimit = isUnderMBLimit({
    totalBytes,
    limitSize: sizeLimitMB,
    conservative: true,
    marginBytes: 0
  });
  const showCaseSummaryNote = extraAttachmentBytes > 0;
  return (
    <Dialog
      title="Select files to attach"
      closeDialog={() => {
        setSelectedDirectMessageRecords([]);
        fileDialog.toggle();
      }}
    >
      {(!caseLoaded)
        ? <div style={{ height: '300px' }}>
            <LoadingPage />
          </div>
        : <>
          <WaitingBox >
            <div className="waitingBoxContent">
              <Icon icon={'warning'}/>
              <h4>Medical images not supported in Direct Messaging</h4>
              <p>
                Radiology and pathology studies — including <strong>DICOM</strong> images — cannot be sent via Direct Messaging due to size and format limitations.
              </p>
            </div>
          </WaitingBox>
          <RecordsManagerDirectMessage
            allowReorder={false}
            disabled={true}
            uploaderRef={uploaderRef}
            caseRequest={caseRequest}
            setCaseRequest={setCaseRequest}
            setMedicalRecords={(updateRecords) => setCaseRequest(_.update('medicalRecords', (prevRecords) => callIfFunction(updateRecords, prevRecords)))}
            caseId={caseRequest.requestCode}
            listMode={true}
            autoGrouping={false}
            patientInfo={caseRequest.patientInfo}
            canOpenWorklist={false}
            isPatient={false}
            role={ADMIN}
            mode={DISPLAY}
            refreshUrl={''}
            selectedDirectMessageRecords={selectedDirectMessageRecords}
            setSelectedDirectMessageRecords={setSelectedDirectMessageRecords}
          />
        </>
      }
      
      {selectedDirectMessageRecords.length > 0 && <div>
        <SizeLine>
          Total size: {totalSize}
          {showCaseSummaryNote && ' (includes case report PDF)'}
        </SizeLine>
        {!totalSizeUnderLimit && <SizeError>
          Total size exceeds {sizeLimitMB} MB limit for Direct Messaging. Please deselect some files{showCaseSummaryNote ? ' or exclude the case report.' : '.'}
        </SizeError>}
      </div>}
      <FooterDiv>
        <Button data-testid="cancel-button-direct-message-record-selection" onClick={() => {
          setSelectedDirectMessageRecords([])
          fileDialog.toggle()
        }}>Cancel</Button>
        <Button highlight 
          disabled={selectedDirectMessageRecords.length === 0 || !totalSizeUnderLimit || isEffectProcessing }
          onClick={() => {
            fileDialog.toggle();
          }}
        >
          Select
        </Button>
      </FooterDiv>
    </Dialog>
  );
};
